import {Component, OnDestroy, OnInit} from '@angular/core';
import {forkJoin, Subject, Subscription} from 'rxjs';
import {HttpErrorResponse} from '@angular/common/http';
import {takeUntil} from 'rxjs/operators';
import {
  BENEFIT_TYPE_LABEL,
  BenefitType, EXPENSE_REPORT_STATUS_LABEL, GetProviderName,
  ORDER_STATUS_LABEL,
  PricingPlan,
  SHIPPING_TYPE_LABEL,
  SUBSCRIPTION_LABEL
} from 'app/core/helpers/constants';
import {BenefitHistoryService} from '../../../core/service/benefit-history.service';
import {GetHistoryOrdersRequest} from '../../../core/requests/GetHistoryOrdersRequest';
import {GetHistoryOrdersResponse} from '../../../core/responses/GetHistoryOrdersResponse';
import {OrdersHistory} from '../../../core/models/orders-history';
import {ExpenseReportHistory} from '../../../core/models/expense-report-history';
import {TranslatePipe} from '@ngx-translate/core';
import {AuthenticationService} from '../../../core/service';
import {ActivatedRoute, Router} from '@angular/router';
import {ToastrService} from 'ngx-toastr';
import {InsuranceService} from '../../../core/service/insurance.service';
import {InsuranceOrderHistory} from '../../../core/models/insurance-order';

@Component({
  selector: 'app-benefits-history',
  templateUrl: './benefits-history.component.html',
  styleUrls: ['./benefits-history.component.scss'],
  providers: [TranslatePipe],
})

export class BenefitsHistoryComponent implements OnInit, OnDestroy {
  private ngUnsubscribe = new Subject();
  private userDashboardSubscription: Subscription;
  private userCompanyInfoSubscription: Subscription;
  public historyBenefits: Array<OrdersHistory> = [];
  public subscriptionBenefitType = BenefitType.Subscription;
  public expenseReportType = 3;
  public insuranceType = 4;
  public selectedBenefitType = 0;
  public status = '';
  public subdomain: string;
  public numberOfOrdersShown = 0;
  public numberOfSubscriptionsShown = 0;
  public numberOfAutoOrdersShown = 0;
  public totalNumberOfOrders: number = 0;
  public totalNumberOfSubscriptionsShown: number = 0;
  public totalNumberOfAutoOrdersShown: number = 0;
  public isFlexPlan = true;
  public hasFixedBenefits = false;
  private userId: string;
  public showSuccessBanner = false;
  public expenseReports: Array<ExpenseReportHistory> = [];
  public numberOfExpenseReportsShown: number = 0;
  public totalNumberOfExpenseReports: number = 0;
  public insuranceOrders: Array<InsuranceOrderHistory> = [];
  public hasInsuranceOrders = false;
  public hasExpenseReports = false;



  constructor(
    private benefitHistoryService: BenefitHistoryService,
    private authenticationService: AuthenticationService,
    private _route: ActivatedRoute,
    private translate: TranslatePipe,
    private _router: Router,
    private _toastrService: ToastrService,
    private router: Router,
    private insuranceService: InsuranceService,
  ) {
    this.subdomain = this._route.snapshot.params['subdomain'];
    this.status = this._route.snapshot.params.status || '';
    this.userDashboardSubscription = this.authenticationService.currentUserInfo.subscribe((x) => {
      this.userId = x.id;
    });
    this.userCompanyInfoSubscription = this.authenticationService.currentUserCompanyInfo.subscribe((x) => {
      this.isFlexPlan = x.pricingPlan === PricingPlan.FLEX;
      this.hasFixedBenefits = x.hasFixedBenefits;
    });
  }

  ngOnInit(): void {
    const justOrdered = this._route.snapshot.queryParamMap.get('justOrdered');
    if (justOrdered === 'true') {
      this.showSuccessBanner = true;
      this._router.navigate([], {
        relativeTo: this._route,
        queryParams: {},
        replaceUrl: true
      });
    }

    this._route.url.subscribe(() => {
      this.handleOrdersBasedOnRoute();
    });

    this.checkHasInsuranceOrders();
    this.checkHasExpenseReports();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next(undefined);
    this.ngUnsubscribe.complete();
  }

  private handleOrdersBasedOnRoute() {
    this.status = this._route.snapshot.paramMap.get('status');

    if (this.status === 'recurrent') {
      this.selectedBenefitType = this.subscriptionBenefitType;
      this.getRecurrentOrders();
    } else if (this.status === 'expense-reports') {
      this.selectedBenefitType = this.expenseReportType;
      this.getExpenseReports();
    } else if (this.status === 'insurance') {
      this.selectedBenefitType = this.insuranceType;
      this.getInsuranceOrders();
    } else {
      this.selectedBenefitType = 0;
      this.getHistoryOrders();
    }
  }

  /**
   * Get all benefit history items
   */
  public getHistoryOrders() {
    const params: GetHistoryOrdersRequest = {
      paginationCriteria: {
        start: 0,
        limit: 10,
      },
    };

    this.benefitHistoryService
      .getAllOrdersHistory(this.userId, params)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (data: GetHistoryOrdersResponse) => {
          this.onGetHistoryBenefitsSuccess(data);
        },
        error: (err) => {
          this.onHistoryBenefitsFail(err);
        }
      });
  }

  public onGetHistoryBenefitsSuccess(data: GetHistoryOrdersResponse) {
    this.historyBenefits = data.responseObject.data.map(e => {
      const createdOnToDate = new Date(e.createdOn);
      return {
        ...e,
        shippingTypeLabel: SHIPPING_TYPE_LABEL[e.shippingType],
        benefitTypeLabel: BENEFIT_TYPE_LABEL[e.benefitType],
        orderStatusLabel: ORDER_STATUS_LABEL[e.orderStatus],
        subscriptionStatusLabel: (e.subscriptionStatus) ? SUBSCRIPTION_LABEL[e.subscriptionStatus] : '-',
        createdOn: createdOnToDate
      };
    });

    this.historyBenefits.forEach((benefit) => {
      benefit.providerName = GetProviderName(benefit.providerName, this.subdomain);
    });

    this.numberOfOrdersShown = data.responseObject.totalResults > data.responseObject.pageSize ?
      data.responseObject.pageSize : this.historyBenefits?.length;
    this.totalNumberOfOrders = data.responseObject.totalResults;

    if (this.userDashboardSubscription) {
      this.userDashboardSubscription.unsubscribe();
    }

    if (this.userCompanyInfoSubscription) {
      this.userCompanyInfoSubscription.unsubscribe();
    }
  }

  public onHistoryBenefitsFail(error: HttpErrorResponse) {
    this.userDashboardSubscription = this.authenticationService.currentUserInfo.subscribe((x) => {
      this.userId = x.id;
    });
    this.userCompanyInfoSubscription = this.authenticationService.currentUserCompanyInfo.subscribe((x) => {
      this.isFlexPlan = x.pricingPlan === PricingPlan.FLEX;
    });
  }

  private getRecurrentOrders() {
    const params: GetHistoryOrdersRequest = {
      paginationCriteria: {
        start: 0,
        limit: 10,
      },
    };

    this.historyBenefits = [];
    this.numberOfSubscriptionsShown = 0;
    this.numberOfAutoOrdersShown = 0;
    this.totalNumberOfSubscriptionsShown = 0;
    this.totalNumberOfAutoOrdersShown = 0;

    forkJoin({
      subscriptions: this.benefitHistoryService.getAllSubscriptionsHistory(this.userId, params),
      autoOrders: this.benefitHistoryService.getAllAutoOrdersHistory(this.userId, params),
    })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: ({subscriptions, autoOrders}) => {
          this.onInitialRecurrentOrdersSuccess(subscriptions);
          this.onInitialRecurrentOrdersSuccess(autoOrders, true);
          this.sortRecurrentOrdersByCreatedOn();

          if (this.userDashboardSubscription) {
            this.userDashboardSubscription.unsubscribe();
          }

          if (this.userCompanyInfoSubscription) {
            this.userCompanyInfoSubscription.unsubscribe();
          }
        },
        error: (err) => {
          this.onGetRecurrentOrdersFail(err);
        }
      });
  }

  private mapRecurrentOrders(data: GetHistoryOrdersResponse): Array<OrdersHistory> {
    const recurringOrders = data.responseObject.data.map(e => {
      const createdOnToDate = new Date(e.createdOn);
      return {
        ...e,
        shippingTypeLabel: SHIPPING_TYPE_LABEL[e.shippingType],
        benefitTypeLabel: BENEFIT_TYPE_LABEL[e.benefitType],
        orderStatusLabel: ORDER_STATUS_LABEL[e.orderStatus],
        subscriptionStatusLabel: (e.subscriptionStatus != null) ? SUBSCRIPTION_LABEL[e.subscriptionStatus] : '-',
        createdOn: createdOnToDate
      };
    });

    recurringOrders.forEach((benefit) => {
      benefit.providerName = GetProviderName(benefit.providerName, this.subdomain);
    });

    return recurringOrders;
  }

  private sortRecurrentOrdersByCreatedOn() {
    this.historyBenefits.sort((a, b) => new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime());
  }

  private onInitialRecurrentOrdersSuccess(data: GetHistoryOrdersResponse, areAutoOrders: boolean = false) {
    const recurringOrders = this.mapRecurrentOrders(data);
    this.historyBenefits.push(...recurringOrders);

    if (areAutoOrders) {
      this.numberOfAutoOrdersShown = recurringOrders.length;
      this.totalNumberOfAutoOrdersShown = data.responseObject.totalResults;
      return;
    }

    this.numberOfSubscriptionsShown = recurringOrders.length;
    this.totalNumberOfSubscriptionsShown = data.responseObject.totalResults;
  }

  private onLoadMoreRecurrentOrdersSuccess(data: GetHistoryOrdersResponse, areAutoOrders: boolean = false) {
    const recurringOrders = this.mapRecurrentOrders(data);
    this.historyBenefits.push(...recurringOrders);

    if (areAutoOrders) {
      this.numberOfAutoOrdersShown += recurringOrders.length;
      this.totalNumberOfAutoOrdersShown = data.responseObject.totalResults;
      return;
    }

    this.numberOfSubscriptionsShown += recurringOrders.length;
    this.totalNumberOfSubscriptionsShown = data.responseObject.totalResults;
  }

  private onGetRecurrentOrdersFail(error: HttpErrorResponse) {
    this.userDashboardSubscription = this.authenticationService.currentUserInfo.subscribe((x) => {
      this.userId = x.id;
    });
    this.userCompanyInfoSubscription = this.authenticationService.currentUserCompanyInfo.subscribe((x) => {
      this.isFlexPlan = x.pricingPlan === PricingPlan.FLEX;
    });
  }

  /**
   * Helpers
   */
  public selectBenefitType(filterType: number) {
    this.showSuccessBanner = false;
    this.selectedBenefitType = filterType;
    if (this.selectedBenefitType === this.subscriptionBenefitType) {
      this.router.navigate(['/' + this.subdomain + '/benefits-history/recurrent']);
    } else if (this.selectedBenefitType === this.expenseReportType) {
      this.router.navigate(['/' + this.subdomain + '/benefits-history/expense-reports']);
    } else if (this.selectedBenefitType === this.insuranceType) {
      this.router.navigate(['/' + this.subdomain + '/benefits-history/insurance']);
    } else {
      this.router.navigate(['/' + this.subdomain + '/benefits-history/all']);
    }
  }

  loadMoreOrders() {
    const params: GetHistoryOrdersRequest = {
      paginationCriteria: {
        start: this.numberOfOrdersShown,
        limit: 10,
      },
    };

    this.numberOfOrdersShown += (this.totalNumberOfOrders - this.numberOfOrdersShown) < 10 ?
      (this.totalNumberOfOrders - this.numberOfOrdersShown) % 10 : 10;

    this.benefitHistoryService
      .getAllOrdersHistory(this.userId, params)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (data: GetHistoryOrdersResponse) => {
          this.onLoadMoreOrdersSuccess(data);
        },
        error: (err) => {
          this.onLoadMoreOrdersFail(err);
        }
      });
  }

  private onLoadMoreOrdersSuccess(data: GetHistoryOrdersResponse) {
    let historyBenefitsToAppend = data.responseObject.data.map(e => {
      const createdOnToDate = new Date(e.createdOn);
      return {
        ...e,
        shippingTypeLabel: SHIPPING_TYPE_LABEL[e.shippingType],
        benefitTypeLabel: BENEFIT_TYPE_LABEL[e.benefitType],
        orderStatusLabel: ORDER_STATUS_LABEL[e.orderStatus],
        subscriptionStatusLabel: (e.subscriptionStatus) ? SUBSCRIPTION_LABEL[e.subscriptionStatus] : '-',
        createdOn: createdOnToDate
      };
    });
    historyBenefitsToAppend.forEach((benefit) => {
      benefit.providerName = GetProviderName(benefit.providerName, this.subdomain);
    });

    this.historyBenefits.push(...historyBenefitsToAppend);
  }

  private onLoadMoreOrdersFail(error: any) {
    error.messages.forEach((errorItem) => {
      this._toastrService.error(
        this.translate.transform(errorItem),
        this.translate.transform('ERROR'),
        {toastClass: 'toast ngx-toastr', closeButton: true, timeOut: 3000}
      );
    });
  }

  loadMoreRecurrentOrders() {
    const subscriptionsParams: GetHistoryOrdersRequest = {
      paginationCriteria: {
        start: this.numberOfSubscriptionsShown,
        limit: 10,
      },
    };

    const autoOrdersParams: GetHistoryOrdersRequest = {
      paginationCriteria: {
        start: this.numberOfAutoOrdersShown,
        limit: 10,
      },
    };

    forkJoin({
      subscriptions: this.benefitHistoryService.getAllSubscriptionsHistory(this.userId, subscriptionsParams),
      autoOrders: this.benefitHistoryService.getAllAutoOrdersHistory(this.userId, autoOrdersParams),
    })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: ({subscriptions, autoOrders}) => {
          this.onLoadMoreRecurrentOrdersSuccess(subscriptions);
          this.onLoadMoreRecurrentOrdersSuccess(autoOrders, true);
          this.sortRecurrentOrdersByCreatedOn();
        },
        error: (err) => {
          this.onGetRecurrentOrdersFail(err);
        }
      });
  }

  public getInsuranceOrders() {
    this.insuranceService
      .getInsuranceOrders(this.userId)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (orders) => {
          this.insuranceOrders = orders || [];
        },
        error: () => {
          this.insuranceOrders = [];
        }
      });
  }

  public checkHasInsuranceOrders() {
    this.insuranceService
      .getInsuranceOrders(this.userId)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (orders) => {
          this.hasInsuranceOrders = (orders || []).length > 0;
        },
        error: () => {
          this.hasInsuranceOrders = false;
        }
      });
  }

  public checkHasExpenseReports() {
    this.benefitHistoryService
      .getMyExpenseReports(this.userId, 0, 1)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (data: any) => {
          this.hasExpenseReports = (data?.responseObject?.totalResults || 0) > 0;
        },
        error: () => {
          this.hasExpenseReports = false;
        }
      });
  }

  public getExpenseReports() {
    this.benefitHistoryService
      .getMyExpenseReports(this.userId, 0, 10)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (data: any) => {
          this.onGetExpenseReportsSuccess(data);
        },
        error: (err) => {
          this.onHistoryBenefitsFail(err);
        }
      });
  }

  private onGetExpenseReportsSuccess(data: any) {
    const reports = data.responseObject.data || [];
    this.expenseReports = reports.map(e => {
      return {
        ...e,
        statusLabel: EXPENSE_REPORT_STATUS_LABEL[e.status],
        createdAt: new Date(e.createdAt),
        reviewedAt: e.reviewedAt ? new Date(e.reviewedAt) : null
      };
    });

    this.numberOfExpenseReportsShown = data.responseObject.totalResults > data.responseObject.pageSize
      ? data.responseObject.pageSize
      : this.expenseReports?.length;
    this.totalNumberOfExpenseReports = data.responseObject.totalResults;
  }

  loadMoreExpenseReports() {
    this.numberOfExpenseReportsShown += (this.totalNumberOfExpenseReports - this.numberOfExpenseReportsShown) < 10 ?
      (this.totalNumberOfExpenseReports - this.numberOfExpenseReportsShown) % 10 : 10;

    this.benefitHistoryService
      .getMyExpenseReports(this.userId, this.expenseReports.length, 10)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (data: any) => {
          const reports = data.responseObject.data || [];
          const mapped = reports.map(e => ({
            ...e,
            statusLabel: EXPENSE_REPORT_STATUS_LABEL[e.status],
            createdAt: new Date(e.createdAt),
            reviewedAt: e.reviewedAt ? new Date(e.reviewedAt) : null
          }));
          this.expenseReports.push(...mapped);
        },
        error: (err) => {
          this.onLoadMoreOrdersFail(err);
        }
      });
  }
}
